import React, { useEffect, useMemo, useRef } from 'react';
import { FolderOpen, CheckSquare, User } from 'lucide-react';
import { useMentionSearch } from '../../hooks/api';

const ALL_TABS = [
  { key: 'user', label: 'Users', icon: User },
  { key: 'task', label: 'Tasks', icon: CheckSquare },
  { key: 'project', label: 'Projects', icon: FolderOpen },
];

function asText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return asText(value.name || value.title || value.display || value.username || '');
  }
  return '';
}

function mentionLabel(item) {
  const display = asText(item?.display);
  if (display && display !== '[object Object]') return display;
  if (item?.type === 'user') {
    return asText(item.subtitle) || asText(item.username) || 'User';
  }
  return asText(item?.subtitle) || asText(item?.name) || 'Untitled';
}

function mentionSubtitle(item, label) {
  const subtitle = asText(item?.subtitle);
  if (!subtitle || subtitle === label || subtitle === '[object Object]') return '';
  return subtitle;
}

const MentionPicker = ({
  query = '',
  activeType = 'task',
  onTypeChange,
  onSelect,
  onClose,
  conversationId,
  allowUserMentions = false,
}) => {
  const tabs = useMemo(
    () => (allowUserMentions ? ALL_TABS : ALL_TABS.filter((t) => t.key !== 'user')),
    [allowUserMentions]
  );
  const resolvedType =
    !allowUserMentions && activeType === 'user' ? 'task' : activeType;
  const { data = [], isLoading } = useMentionSearch(
    resolvedType,
    query,
    true,
    conversationId
  );
  const containerRef = useRef(null);

  useEffect(() => {
    if (!allowUserMentions && activeType === 'user') {
      onTypeChange?.('task');
    }
  }, [allowUserMentions, activeType, onTypeChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 right-0 mb-2 rounded-xl border border-secondary-200 bg-white shadow-lg dark:border-secondary-700 dark:bg-secondary-800 z-50 overflow-hidden animate-fade-in"
      data-testid="mention-picker"
    >
      <div className="flex border-b border-secondary-200 dark:border-secondary-700">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => onTypeChange?.(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium transition-colors ${
              resolvedType === key
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50 dark:text-primary-400 dark:bg-primary-900/20'
                : 'text-secondary-500 hover:text-secondary-800 dark:text-secondary-400 dark:hover:text-secondary-100'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>
      <div className="max-h-48 overflow-y-auto">
        {isLoading && (
          <div className="px-3 py-4 text-sm text-secondary-500 text-center">Searching…</div>
        )}
        {!isLoading && data.length === 0 && (
          <div className="px-3 py-4 text-sm text-secondary-500 text-center">No results</div>
        )}
        {!isLoading &&
          data.map((item) => {
            const label = mentionLabel(item);
            const subtitle = mentionSubtitle(item, label);
            return (
              <button
                key={`${item.type}-${item.id}`}
                type="button"
                onClick={() => onSelect?.({ ...item, display: label })}
                className="w-full text-left px-3 py-2.5 hover:bg-secondary-50 dark:hover:bg-secondary-700/60 transition-colors min-h-[44px]"
              >
                <div className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                  {label}
                </div>
                {subtitle && (
                  <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                    {subtitle}
                  </div>
                )}
              </button>
            );
          })}
      </div>
    </div>
  );
};

export default MentionPicker;
